// Trade accounts (F-TRD-N1): labels and the small arithmetic the screens
// share. Totals here are only a preview — the backend prices and totals every
// delivery (internal/trade), and what it returns is what is shown afterwards.

import type { TradeAccount, TradeBillingCycle, TradeBranch, TradeDeliveryStatus, TradeReturnsMode } from '@/types/trade'

export const STATUS_LABEL: Record<TradeDeliveryStatus, string> = {
  draft: 'Draft',
  confirmed: 'Confirmed',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

export function statusTone(s: TradeDeliveryStatus): string {
  switch (s) {
    case 'draft':
      return 'bg-warning-surface text-warning-fg'
    case 'confirmed':
      return 'bg-info-surface text-info-fg'
    case 'delivered':
      return 'bg-success-surface text-success-fg'
    default:
      return 'bg-muted text-muted-foreground line-through'
  }
}

export const CYCLE_LABEL: Record<TradeBillingCycle, string> = {
  semi_monthly: 'Every 15 days (1st–15th, 16th–end)',
  weekly: 'Weekly (Monday–Sunday)',
  monthly: 'Monthly',
}

export const CYCLE_SHORT: Record<TradeBillingCycle, string> = {
  semi_monthly: 'Every 15 days',
  weekly: 'Weekly',
  monthly: 'Monthly',
}

export const RETURNS_LABEL: Record<TradeReturnsMode, string> = {
  none: 'Sold outright',
  sale_or_return: 'Sale or return',
}

/** "morning" → "Morning"; a free-text slot is shown as typed. */
export function slotLabel(slot: string): string {
  if (slot === 'morning') return 'Morning'
  if (slot === 'evening') return 'Evening'
  return slot
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** "2026-10-12" → "Mon 12 Oct". */
export function dayLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return iso
  return `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
}

/** "2026-10-01".."2026-10-15" → "1–15 Oct"; across months "26 Oct – 1 Nov". */
export function periodLabel(start: string, end: string): string {
  const s = new Date(`${start}T12:00:00Z`)
  const e = new Date(`${end}T12:00:00Z`)
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return `${start} – ${end}`
  if (s.getUTCMonth() === e.getUTCMonth()) return `${s.getUTCDate()}–${e.getUTCDate()} ${MONTHS[e.getUTCMonth()]}`
  return `${s.getUTCDate()} ${MONTHS[s.getUTCMonth()]} – ${e.getUTCDate()} ${MONTHS[e.getUTCMonth()]}`
}

/** The fare a new delivery starts with: the branch's own, else the client's. */
export function defaultFare(account: Pick<TradeAccount, 'default_fare'> | undefined, branch: Pick<TradeBranch, 'default_fare'> | undefined): number {
  if (branch && branch.default_fare != null) return branch.default_fare
  return account?.default_fare ?? 0
}

const round2 = (n: number) => Math.round(n * 100) / 100

export interface PreviewLine {
  qty: number
  price: number | null
}

/** Mirrors trade.ComputeTotals: unpriced lines count as missing, not as 0. */
export function previewTotals(lines: PreviewLine[], fare: number) {
  let subtotal = 0
  let missing = 0
  for (const l of lines) {
    if (l.qty < 1) continue
    if (l.price == null) {
      missing++
      continue
    }
    subtotal += round2(l.qty * l.price)
  }
  subtotal = round2(subtotal)
  const f = round2(Number.isFinite(fare) && fare > 0 ? fare : 0)
  return { subtotal, fare: f, total: round2(subtotal + f), missing }
}

/** A typed money field: '' → null, otherwise a non-negative number or NaN. */
export function parseMoney(s: string): number | null {
  const t = s.replace(/,/g, '').trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) && n >= 0 ? round2(n) : Number.NaN
}

export function parseQty(s: string): number {
  const n = Math.floor(Number(s))
  return Number.isFinite(n) && n > 0 ? n : 0
}
